import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { CacheManager } from './CacheManager';
import { PropertyDocument } from '../types';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import FileDrop from './FileDrop';

export const VaultView = () => {
  const [documents, setDocuments] = useState<PropertyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'documents'), orderBy('dateAdded', 'desc'));
    const unsub = onSnapshot(q,
      (snapshot) => {
        const docs: PropertyDocument[] = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        } as PropertyDocument));
        setDocuments(docs);
        setLoading(false);
        setError(null);
      },
      (err) => {
        handleFirestoreError(err, OperationType.LIST, 'documents');
        setError('Could not load documents. Check your connection and try again.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const isOld = (dateStr: string) => {
    const date = new Date(dateStr);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return date < oneYearAgo;
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-[48px] border border-stone-200 p-6 sm:p-10 shadow-sm">
        <h3 className="text-2xl sm:text-3xl font-black text-bark mb-4 sm:mb-6">Document Vault</h3>
        <p className="text-stone-500 mb-8">
          Access your certifications, storm mitigation plans, and property documents securely.
          {documents.length > 0 && <span className="text-gecko font-bold"> {documents.length} documents on file.</span>}
        </p>

        <div className="mb-8">
          <FileDrop title="Upload a property document" />
        </div>

        {loading && (
          <div className="flex items-center space-x-3 text-stone-400 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-bold text-sm">Loading documents...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-center space-x-2 text-red-700 text-sm font-bold">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && documents.length === 0 && (
          <div className="text-center py-12 bg-stone-50 rounded-3xl mb-8">
            <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-bold mb-1">No documents yet</p>
            <p className="text-stone-400 text-sm">Upload permits, certifications, and property documents to get started.</p>
          </div>
        )}

        <div className="space-y-4 mb-10">
          {documents.map((doc) => {
            const old = isOld(doc.dateAdded);
            return (
              <div
                key={doc.id}
                className={`p-4 sm:p-6 rounded-3xl border flex items-center justify-between ${
                  old ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-100'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className={`p-2 sm:p-3 rounded-xl shrink-0 ${
                    old ? 'bg-amber-100 text-amber-600' : 'bg-gold/10 text-gold'
                  }`}>
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-black text-bark text-sm sm:text-base truncate">{doc.title}</h4>
                    <p className="text-xs sm:text-sm text-stone-500 truncate">
                      {doc.type} · {doc.summary} · Added {doc.dateAdded}
                    </p>
                  </div>
                </div>
                {doc.url && (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[9px] font-black uppercase tracking-widest text-palm hover:underline shrink-0 ml-3"
                  >
                    Open
                  </a>
                )}
                {old && (
                  <div className="flex items-center gap-2 text-amber-700 font-bold text-[10px] sm:text-xs uppercase bg-amber-200/50 px-3 sm:px-4 py-2 rounded-full shrink-0 ml-3">
                    <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" /> Needs Refresh
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <CacheManager />
      </div>
    </div>
  );
};
